import { useEffect, useMemo, useState } from "react";
import dayjs from "@/lib/dayjs.config";
import { DATE_FORMAT } from "@/shared/constants";
import { usePermission } from "@/shared/hooks/usePermission";
import { toUTC } from "@/shared/utils/date.util";
import { UseTrialExpiryResult } from "../type/Header.type";

export const useTrialExpiry = (): UseTrialExpiryResult => {
  const { isSuperAdmin, isTrialUser, userExpiry } = usePermission();
  const [nowTimestamp, setNowTimestamp] = useState<number>(() => Date.now());

  const showIndicator = Boolean(isTrialUser && userExpiry && !isSuperAdmin);

  const remainingMilliseconds = useMemo((): number => {
    if (!showIndicator || !userExpiry) {
      return 0;
    }

    return userExpiry.getTime() - nowTimestamp;
  }, [nowTimestamp, showIndicator, userExpiry]);

  useEffect((): (() => void) | void => {
    if (!showIndicator || !userExpiry) {
      return;
    }

    const refreshNow = (): void => {
      setNowTimestamp(Date.now());
    };

    const utcNow = dayjs.utc(nowTimestamp);
    const isLastFiveMinutes = remainingMilliseconds <= 5 * 60 * 1000;

    if (remainingMilliseconds <= 0) {
      return;
    }

    if (isLastFiveMinutes) {
      const intervalId = window.setInterval(refreshNow, 1000);

      return (): void => {
        window.clearInterval(intervalId);
      };
    }

    const millisecondsUntilCountdown = remainingMilliseconds - 5 * 60 * 1000;
    const nextUtcDayStart = utcNow.add(1, "day").startOf("day");
    const millisecondsUntilNextUtcDay = nextUtcDayStart.diff(utcNow);
    const nextRefreshDelay = Math.min(
      millisecondsUntilCountdown,
      millisecondsUntilNextUtcDay,
    );
    const timeoutId = window.setTimeout(
      refreshNow,
      Math.max(nextRefreshDelay, 1),
    );

    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, [nowTimestamp, remainingMilliseconds, showIndicator, userExpiry]);

  const isExpired = useMemo((): boolean => {
    if (!showIndicator || !userExpiry) {
      return false;
    }

    return remainingMilliseconds <= 0;
  }, [remainingMilliseconds, showIndicator, userExpiry]);

  const formattedExpiry = useMemo((): string => {
    if (!showIndicator || !userExpiry) {
      return "";
    }

    return toUTC(userExpiry, DATE_FORMAT.dateTime);
  }, [showIndicator, userExpiry]);

  const badgeLabel = useMemo((): string => {
    if (!showIndicator || !userExpiry) {
      return "";
    }

    if (isExpired) {
      return "Trial Expired";
    }

    if (remainingMilliseconds <= 5 * 60 * 1000) {
      const totalSeconds = Math.max(
        Math.floor(remainingMilliseconds / 1000),
        0,
      );
      const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
      const seconds = String(totalSeconds % 60).padStart(2, "0");

      return `Trial expires in ${minutes}:${seconds}`;
    }

    const today = dayjs.utc(nowTimestamp).startOf("day");
    const expiryDay = dayjs.utc(userExpiry).startOf("day");

    if (today.isSame(expiryDay, "day")) {
      return "Trial Ends Today";
    }

    const daysLeft = expiryDay.diff(today, "day");
    const dayLabel = daysLeft === 1 ? "Day" : "Days";

    return `Trial ${daysLeft} ${dayLabel} left`;
  }, [
    isExpired,
    nowTimestamp,
    remainingMilliseconds,
    showIndicator,
    userExpiry,
  ]);

  return {
    showIndicator,
    badgeLabel,
    formattedExpiry,
    isExpired,
  };
};
